import React, { useCallback, useMemo, useRef } from "react";
import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { defaultConfig } from "./default_config";
import { PivotControl } from "../../constants/pivot_control_options";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { getPurchaseOrderExportTableData } from "../../utils/purchase_order_export_table_data";

// Container for Peach & Lily PO Export using common pattern
export const PurchaseOrderExportPeachAndLilyContainer = () => {
  // Use a single ref to store all input values (no re-renders on edit)
  const inputsRef = useRef<Record<string, Record<string, string>>>({
    quantity: {},
    rate: {},
    po: {},
    po_deliver: {},
    shipment_method: {},
    memo: {},
    override_po_print_slip_name: {},
  });

  // Single generic function to update any input field
  const updateInputs = (
    field: string,
    updater: React.SetStateAction<Record<string, string>>
  ) => {
    if (typeof updater === "function") {
      inputsRef.current[field] = updater(inputsRef.current[field] || {});
    } else {
      inputsRef.current[field] = updater;
    }
  };

  // Stable helpers and config objects to avoid unnecessary rerenders/resubscriptions
  const getInputs = useCallback(
    (field: string) => inputsRef.current[field] || {},
    []
  );

  const purchaseOrderConfig = useMemo(
    () => ({
      getInputs,
      setInputs: updateInputs,
      editableFields: [
        "quantity",
        "rate",
        "po",
        "po_deliver",
        "shipment_method",
        "memo",
        "override_po_print_slip_name",
      ],
    }),
    [getInputs]
  );

  return (
    <TransformedCustomViewContainer
      viewControllerConfig={useMemo(
        () => ({
          datasetName: "purchase_order_plan",
          transformId: "p_l_purchase_order_export", // Our custom P&L transform with exact formatting
          folder: "outputs",
          title: "PO Export",
          dateHierarchyColumnFilter: {
            Date: "Date",
          },
          columnHeaderMap: {
            "PO SHIP": "PO ship",
            "Override PO Print Slip Name": "override PO print slip name",
          },
          excludedSegmentationsFilter: [],
          measureColumnFilter: null,
          excludedFilterColumnFilter: [],
          showControls: [
            PivotControl.DatePeriodPicker,
            PivotControl.StoreAsDefault,
            PivotControl.Download,
          ],
          paramsToTransformArgs: ((
            params: DatePivotParams
          ): Record<string, any> => ({
            min_date: params.minDate,
            max_date: params.maxDate,
            filters: [],
          })) as (params: DatePivotParams) => Record<string, any>,
          tableType: "flat",
          purchaseOrderConfig,
          getMatrix: getPurchaseOrderExportTableData,
          // Memo deps: only constants and stable refs
        }),
        [purchaseOrderConfig]
      )}
      defaultConfig={defaultConfig}
    />
  );
};
